import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  ChevronDown,
  Filter,
  Globe,
  Lightbulb,
  Loader2,
  Plus,
  RefreshCcw,
  Sparkles,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AgentPageShell, { EmptyState } from '../../components/agent/AgentPageShell';
import ConfidenceBar from '../../components/agent/ConfidenceBar';
import { PriorityBadge } from '../../components/agent/StatusBadge';
import { cn } from '../../lib/utils';
import { fetchGaps, updateGap } from '../../lib/agentApi';

const PRIORITIES = ['All', 'High', 'Medium', 'Low'];
const LANGUAGES = [
  ['Both', 'Both'],
  ['en', 'English'],
  ['ar', 'Arabic'],
];

const KnowledgeGapsPage = () => (
  <AgentPageShell
    title="Knowledge Gap Insights"
    description="Questions users asked that your documentation does not answer well"
    icon={Lightbulb}
    breadcrumb={['Knowledge Gaps']}
  >
    {({ agent, orgId }) => <GapsBody key={agent.id} agent={agent} orgId={orgId} />}
  </AgentPageShell>
);

const GapsBody = ({ agent, orgId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priority, setPriority] = useState('All');
  const [language, setLanguage] = useState('Both');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchGaps(orgId, agent.id, { priority, language }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load knowledge gaps');
    } finally {
      setLoading(false);
    }
  }, [orgId, agent.id, priority, language]);

  useEffect(() => {
    load();
  }, [load]);

  const markGap = async (gap, status) => {
    try {
      await updateGap(orgId, agent.id, gap.id, status);
      toast.success(status === 'known' ? 'Marked as known' : 'Marked as addressed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update the gap');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Analysing conversations…
      </div>
    );
  }
  if (!data) return null;

  const { gaps, themes, summary, confidence_threshold: threshold } = data;

  if (summary.total === 0) {
    return (
      <EmptyState
        icon={CheckCircle2}
        title="No knowledge gaps found"
        body={`Every answered question scored at or above this agent's ${threshold}% confidence threshold. Gaps appear here as users ask things your documentation doesn't cover.`}
        action={
          <Button variant="outline" onClick={load}>
            <RefreshCcw className="w-4 h-4 mr-2" />
            Re-analyse
          </Button>
        }
      />
    );
  }

  const chartData = gaps.slice(0, 8).map((gap) => ({
    name: gap.topic.length > 16 ? `${gap.topic.slice(0, 16)}…` : gap.topic,
    frequency: gap.frequency,
    priority: gap.priority,
  }));

  const barColor = (p) =>
    p === 'High'
      ? 'hsl(var(--destructive))'
      : p === 'Medium'
      ? 'hsl(var(--chart-3))'
      : 'hsl(var(--chart-2))';

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground">
          Computed from sessions scoring below this agent's {threshold}% confidence threshold.
        </p>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCcw className="w-4 h-4 mr-2" />
          Re-analyse
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard label="Total gaps" value={summary.total} trend="Detected" tone="text-foreground" />
        <SummaryCard label="High priority" value={summary.high} trend="Needs action" tone="text-destructive" />
        <SummaryCard label="Medium priority" value={summary.medium} trend="Review soon" tone="text-amber-500" />
        <SummaryCard label="Low priority" value={summary.low} trend="Optimisations" tone="text-emerald-500" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-muted/30 p-3 rounded-lg border border-border">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filter by:</span>
        </div>
        <div className="flex items-center gap-1.5 border-r border-border pr-4">
          {PRIORITIES.map((p) => (
            <Button
              key={p}
              variant={priority === p ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs px-3"
              onClick={() => setPriority(p)}
            >
              {p}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5 text-muted-foreground" />
          {LANGUAGES.map(([value, label]) => (
            <Button
              key={value}
              variant={language === value ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 text-xs px-3"
              onClick={() => setLanguage(value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* Chart */}
          {chartData.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold">Trending Gaps</h3>
                    <p className="text-xs text-muted-foreground">
                      Most frequently asked uncovered topics
                    </p>
                  </div>
                  <Badge className="bg-primary/10 text-primary border-primary/20">
                    Frequency vs priority
                  </Badge>
                </div>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                        interval={0}
                        angle={-15}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--accent))', opacity: 0.2 }}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          borderColor: 'hsl(var(--border))',
                          borderRadius: '8px',
                          fontSize: '12px',
                        }}
                      />
                      <Bar dataKey="frequency" radius={[4, 4, 0, 0]} barSize={36}>
                        {chartData.map((entry, i) => (
                          <Cell key={i} fill={barColor(entry.priority)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Themes */}
          <div className="space-y-4">
            <h2 className="text-lg font-bold">Gaps by Theme</h2>
            {themes.map((theme) => {
              const isOpen = expanded[theme.title] !== false;
              const border =
                theme.priority === 'High'
                  ? 'border-destructive'
                  : theme.priority === 'Medium'
                  ? 'border-amber-500'
                  : 'border-emerald-500';

              return (
                <div key={theme.title} className={cn('border-l-4 rounded-r-lg overflow-hidden', border)}>
                  <button
                    onClick={() =>
                      setExpanded((p) => ({ ...p, [theme.title]: p[theme.title] === false }))
                    }
                    className="w-full flex items-center justify-between p-4 bg-card hover:bg-accent/5 transition-colors border-y border-r border-border"
                  >
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-semibold">{theme.title}</h3>
                      <PriorityBadge priority={theme.priority} />
                      <span className="text-xs text-muted-foreground">
                        {theme.count} gap{theme.count === 1 ? '' : 's'}
                      </span>
                    </div>
                    <ChevronDown
                      className={cn(
                        'w-5 h-5 text-muted-foreground transition-transform',
                        !isOpen && '-rotate-90'
                      )}
                    />
                  </button>

                  {isOpen && (
                    <div className="p-4 bg-background/50 border-r border-b border-border space-y-3">
                      {theme.gaps.map((gap) => (
                        <div
                          key={gap.id}
                          className="group flex flex-col md:flex-row md:items-center justify-between p-4 rounded-lg border border-border bg-card/50 hover:bg-accent/5 transition-colors gap-4"
                        >
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4
                                className="font-medium"
                                dir={gap.language === 'ar' ? 'rtl' : 'ltr'}
                              >
                                {gap.topic}
                              </h4>
                              <Badge variant="secondary" className="font-normal">
                                asked {gap.frequency}×
                              </Badge>
                            </div>
                            {gap.suggested_doc && (
                              <p className="text-sm text-muted-foreground">
                                Suggested: <span className="italic">"{gap.suggested_doc}"</span>
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                            <div className="w-full md:w-32">
                              <ConfidenceBar value={gap.confidence} threshold={threshold} />
                            </div>
                            <div className="flex items-center gap-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 gap-1.5"
                                onClick={() => markGap(gap, 'known')}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Known
                              </Button>
                              <Button size="sm" className="h-8 gap-1.5" asChild>
                                <Link to="/dashboard/spaces">
                                  <Plus className="w-3.5 h-3.5" />
                                  Create doc
                                </Link>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right rail */}
        <div className="space-y-6">
          <Card className="border-primary/20 bg-primary/[0.02]">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-bold">Quick Wins</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                The highest-impact gaps, ranked by how often they are asked against how poorly
                they are covered.
              </p>
              <div className="space-y-3">
                {gaps.slice(0, 5).map((gap) => (
                  <div
                    key={gap.id}
                    className="p-3 rounded-lg border border-border bg-card hover:border-primary/40 transition-colors"
                  >
                    <p
                      className="text-sm font-medium leading-tight mb-1 line-clamp-2"
                      dir={gap.language === 'ar' ? 'rtl' : 'ltr'}
                    >
                      {gap.topic}
                    </p>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                      <span>{gap.frequency}× asked</span>
                      <span>{gap.confidence}% confidence</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/30">
            <CardContent className="p-6 space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-bold">What this means</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This agent answers only from your selected spaces. Every gap here is a question
                your users asked that your documentation could not answer confidently — write
                the doc, re-sync, and the gap closes.
              </p>
              <Button className="w-full" asChild>
                <Link to="/dashboard/agent/knowledge-sources">Review knowledge sources</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, trend, tone }) => (
  <Card>
    <CardContent className="p-5">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
        {label}
      </p>
      <div className="flex items-baseline justify-between">
        <div className={cn('text-3xl font-bold', tone)}>{value}</div>
        <Badge variant="outline" className="text-[10px] font-bold">
          {trend}
        </Badge>
      </div>
    </CardContent>
  </Card>
);

export default KnowledgeGapsPage;
